import { get } from 'lodash';
import qs from 'query-string';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { TEMPLATES_PER_PAGE } from 'routes/constants';
import {
  ActionType,
  MutationType,
  SurveyType,
  SurveysResponseType,
} from '../../../../../shared/types';
import request from '../../../../../utils/request';
import i18n from '../../../../../i18n';

export const useGetTemplates = ({
  callback,
  pageToken = '',
}: MutationType & {
  pageToken?: string;
}) => {
  return useQuery<SurveysResponseType>(
    'templates',
    async () => {
      const newURL = qs.stringifyUrl({
        url: '/surveyserv/templates/',
        query: {
          page_size: TEMPLATES_PER_PAGE,
          ...(pageToken ? { next_token: pageToken } : {}),
        },
      });
      const data: SurveysResponseType = await request(newURL);
      const countURL = get(data, ['_links', 'count', 'href'], '');
      const countReqURL = countURL ? countURL.split('/api/v1')?.[1] : '';
      let countResponse: { count: number } | undefined;
      if (countReqURL) {
        countResponse = await request(countReqURL);
      }
      return {
        ...(data || {}),
        _links: { ...(data._links || {}), count: countResponse?.count || 0 },
      };
    },
    {
      refetchOnMount: true,
      refetchOnWindowFocus: true,
      onSuccess: (response) => {
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
    },
  );
};

export const useGetTemplateById = ({
  templateId,
  callback,
}: MutationType & {
  templateId: string;
}) => {
  return useQuery(
    ['templates', templateId],
    async () => {
      if (!templateId) return;
      const data: SurveyType = await request(
        `/surveyserv/templates/${templateId}/`,
      );
      return data;
    },
    {
      enabled: Boolean(templateId),
      onSuccess: (response) => {
        if (callback) callback(true, response);
      },
      onError: (error) => {
        if (callback) callback(false, error);
      },
      cacheTime: 0,
    },
  );
};

type SaveTemplateType = ActionType & {
  payload: SurveyType;
  id?: string;
  originSurveyId?: string | null;
};

export const useSaveTemplate = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation<SurveyType, any, SaveTemplateType>(
    async ({ payload, id, originSurveyId }) => {
      const data: SurveyType = await request(
        id
          ? `/surveyserv/templates/${id}`
          : qs.stringifyUrl({
              url: '/surveyserv/templates',
              query: {
                ...(originSurveyId ? { origin_survey_id: originSurveyId } : {}),
              },
            }),
        payload,
        id ? 'PUT' : 'POST',
      );
      return data;
    },
    {
      onSuccess: (response: SurveyType, { id }) => {
        queryClient.setQueryData(['templates', `${response.id}`], response);
        queryClient.invalidateQueries(['templates']);
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: i18n.t(
              id
                ? 'template.message.saved_success'
                : 'template.message.created_success',
            ),
          });
        if (callback) callback(true, response);
      },
      onError: (err: any, variable: any) => {
        if (callback) callback(false, err, variable);
      },
    },
  );
};

export const useDeleteTemplate = ({ setToast, callback }: MutationType) => {
  const queryClient = useQueryClient();
  return useMutation(
    async (template: SurveyType) => {
      const data = await request(
        `/surveyserv/templates/${template.id}`,
        {},
        'DELETE',
      );
      return data;
    },
    {
      onSuccess: (response, template) => {
        queryClient.setQueryData('templates', (old: any) => {
          const newData = { ...(old || {}) };
          if (old.surveys && old.surveys.length > 0) {
            newData.surveys = old.surveys.filter(
              (o: any) => o.id !== template.id,
            );
          }
          return newData;
        });
        queryClient.invalidateQueries('templates');
        if (setToast)
          setToast({
            type: 'success',
            delay: 3000,
            text: i18n.t('template.message.deleted'),
          });
        if (callback) callback(true, response, template);
      },
      onError: (error) => {
        if (setToast)
          setToast({
            type: 'error',
            delay: 3000,
            text: i18n.t('template.message.delete_failed'),
          });
        if (callback) callback(false, error);
      },
    },
  );
};
